/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {ReportService} from '../../../../services/report.service';
import {ReportProvider, ReportLinksProvider } from '../../../../providers';
import {Report, Link, Helpers} from '../../../../model';
import {LogService, StageService} from '../../../../services';
import {BsModalService, BsModalRef} from 'ngx-bootstrap/modal';
import {Router, ActivatedRoute} from '@angular/router';
import { Subject } from 'rxjs';
import { EditLinkModal, ConfirmDeleteLinkModal } from '../modals';
import {LinkType} from '../../../../model/report';
import { ClipboardService } from 'ngx-clipboard';
import { ModuleType } from 'src/app/model/enums/module.type';
import { SupplementalLinkModal } from '../modals/supplemental-link/supplemental-link.modal';
import {BVQReportProvider} from "../../../../providers/bvq";

@Component({
  selector: 'page-report-links',
  templateUrl: 'report.links.page.html',
  styleUrls: ['report.links.page.scss']
})
export class ReportLinksPage implements OnInit, OnDestroy {
  currentReport: Report = new Report();
  private reportSub: any;
  totalItems = 0;
  currentPage = 1;
  links: Link[] = [];
  dateCreated: Date = new Date();

  selectAll = false;
  isLoading = false;

  editModal: BsModalRef;
  deleteModal: BsModalRef;
  supplementalLinkModel:BsModalRef;

  createDeleteFlag = false;

  LinkType = LinkType;
  moduleType: ModuleType = ModuleType.PMA;
  ModuleType = ModuleType;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private modalService: BsModalService,
    private stageService: StageService,
    private reportLinkProvider: ReportLinksProvider,
    private clipboardService: ClipboardService,
    private bvqReportProvider: BVQReportProvider,
    private logService : LogService
  ) {}

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;

      if (this.currentReport) {
        this.createDeleteFlag  = this.currentReport.STAGE.NAME === 'Report Submitted';
      }

      this.loadLinks();
    });


    this.moduleType = this.route.snapshot.data.pma ? ModuleType.PMA : ModuleType.BVQ;

  }

  ngOnDestroy(): void {
    if (this.reportSub) this.reportSub.unsubscribe();
  }

  private loadLinks(): void {
    if (this.currentReport) {
      this.isLoading = true;
      this.reportLinkProvider.getLinks(this.currentReport)
        .subscribe(
          data => {
            if (data.success) {
              this.links = Link.parseArray(data.result);
            }
            this.isLoading = false;
          }, () => {
            this.isLoading = false;
          });
    }
  }

  onCreateLink(): void {
    this.reportLinkProvider.createLink(this.currentReport, this.moduleType).subscribe(data => {
      if (data.success) {
        this.links.push(Link.parse(data.result));
        this.totalItems = this.links.length;
      }
    });
  }

  onDeleteLink(link: Link, index: number): void {
    this.deleteModal = this.modalService.show(ConfirmDeleteLinkModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.deleteModal.content.subject = ackSubject;

    ackSubject.subscribe(value => {
      if (value) {
        this.reportLinkProvider.deleteLink(this.currentReport, link.ID)
          .subscribe(data => {
            if (data.success) {
              this.links.splice(index, 1);
            }
          });
      }
    });
  }

  onEditLink(link: Link, index: number): void {
    this.editModal = this.modalService.show(EditLinkModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.editModal.content.subject = ackSubject;
    this.editModal.content.link = link;
    this.editModal.content.expiryDate = link.DATE_EXPIRES ? new Date(link.DATE_EXPIRES) : new Date();
    this.editModal.content.currentReport = this.currentReport;

    ackSubject.subscribe(value => {
      if (value) {
        this.links[index] = Link.parse(value);
      }
    });
  }

  pageChanged(event: any): void {
    if (event.page !== this.currentPage)
      this.loadLinks();
  }

  get hostName(): string {
    return window.location.protocol + '//' + window.location.host.replace('4200', '5000');
  }

  get hostPath(): string {
    return '/link/';
  }

  get bvqHostPath(): string {
    return '/link/ask/';
  }

  getTrackerLinkURL(link: Link): string {
    return `${this.hostName}/tracker/${link.ID}`;
  }

  copy(link: Link): void{
    this.clipboardService.copyFromContent(this.hostName + this.hostPath + link.HASH);

    link.copied = true;
    setTimeout(() => {
      link.copied = false;
    }, 1500);
  }

  onCreateSupplementalLink(){
    this.supplementalLinkModel = this.modalService.show(SupplementalLinkModal, Helpers.getModalConfig());
    const ackSubject = new Subject<boolean>();
    this.supplementalLinkModel.content.subject = ackSubject;
    ackSubject.subscribe(value => {
      if (value) {
      }
    });
  }

  /** Send follow up */
  sendFollowUp(link: Link): void {
    this.bvqReportProvider.sendFollowUp(this.currentReport.ID, link.HASH)
      .subscribe(data => {
        if (!data.success) {
          this.logService.error(data);
        }
      });
  }

  /** Send follow up */
  disableEnableFollowUp(link: Link): void {
    this.bvqReportProvider.disableFollowUp(this.currentReport.ID, !link.DISABLE_FOLLOWUP, link.HASH)
      .subscribe(data => {
        if (data.success) {
          link.DISABLE_FOLLOWUP = !link.DISABLE_FOLLOWUP;
        }
      });
  }
}
